import React, { useEffect, useState } from 'react';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { supabaseService } from '@/shared/services/supabaseService';
import { supabaseClient } from '@/shared/services/client';
import FullPageSkeleton from '@/shared/components/loading/FullPageSkeleton';

interface AppInitializerProps {
  onInitialized?: () => void;
  onError?: (error: string) => void;
  children?: React.ReactNode;
}

/**
 * App initialization component
 * Handles service initialization
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ onInitialized, onError, children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // Initialize services
    const initializeServices = async () => {
      try {
        // Check if services are available
        if (!firebaseAuthService || !supabaseService) {
          throw new Error('Required services are not available');
        }

        // Initialize Firebase and wait for auth state
        await firebaseAuthService.initialize();

        // Get token getter and UID getter
        const tokenGetter = () => firebaseAuthService.getIdToken();

        // Create a smarter UID getter that checks the auth instance directly
        const uidGetter = async () => {
          // Try currentFirebaseUser first (cached)
          const directUid = firebaseAuthService.getUid();
          if (directUid) {
            return directUid;
          }

          // Fallback: get from auth instance directly
          const auth = firebaseAuthService.getAuth();
          if (auth?.currentUser?.uid) {
            return auth.currentUser.uid;
          }

          return null;
        };

        // Configure Supabase to use Firebase tokens
        supabaseClient.setFirebaseTokenGetter(tokenGetter);
        await supabaseClient.setFirebaseUidGetter(uidGetter);

        // Verify services are healthy
        const isHealthy = supabaseService.isHealthy();

        if (!isHealthy) {
          throw new Error('Required services are not available. Please check your configuration.');
        }

        console.log('Using real services (Supabase + Firebase)');

        setInitError(null);
        setIsInitialized(true);
        if (onInitialized) {
          onInitialized();
        }
      } catch (error) {
        console.error('Error initializing services:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error during initialization';
        setInitError(errorMessage);
        setIsRetrying(false);
        if (onError) {
          onError(errorMessage);
        }
      }
    };

    initializeServices();
  }, [onInitialized, onError]);

  // Handle retry without full page reload
  const handleRetry = () => {
    setIsRetrying(true);
    setInitError(null);
    setIsInitialized(false);

    // Re-trigger initialization by setting isInitialized to false
    // The useEffect will re-run when isInitialized changes
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  if (!isInitialized) {
    return <FullPageSkeleton message="Initializing..." />;
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="max-w-md p-4 bg-error-50 border border-error-200 rounded-md">
            <p className="text-error-600 text-sm">Initialization Error: {initError}</p>
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="mt-2 px-4 py-2 bg-error-600 text-white rounded-md hover:bg-error-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppInitializer;
